import { FunctionComponent, useEffect, useState } from 'react'
import {
  Button,
  CardContentGrid,
  CollapsibleCard,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  rucksack,
} from '@black-bear-energy/rucksack'
import { CardContentField } from './CardContentField'
import { EditableTextWithNAOption } from './EditableTextWithNAOption'
import {
  SiteRFPDetails,
  TenantConsentStatusAPIResponse,
} from '@black-bear-energy/black-bear-energy-common'
import { CheckRounded, EditRounded } from '@mui/icons-material'
import { useUpdateSiteRequirements } from 'api/hooks/useUpdateSiteRequirements'

const emptyValue = 'empty'

type OwnersAssociationSelection = 'yes' | 'no' | typeof emptyValue

/** Display text for each tenant consent status, in the order the client works through them. */
const tenantConsentLabels: Record<TenantConsentStatusAPIResponse, string> = {
  [TenantConsentStatusAPIResponse.NoAction]: 'No action taken',
  [TenantConsentStatusAPIResponse.AskedIfRequired]: 'Asked if required',
  [TenantConsentStatusAPIResponse.RequiredNoAction]:
    'Required, not yet requested',
  [TenantConsentStatusAPIResponse.Requested]: 'Requested',
  [TenantConsentStatusAPIResponse.Received]: 'Received',
  [TenantConsentStatusAPIResponse.NotRequired]: 'Not required',
}

export const SiteRequirementsCard: FunctionComponent<
  SiteRequirementsCardProps
> = ({ siteRFPDetails, isFetching }) => {
  const [isEditing, setIsEditing] = useState(false)
  const { mutate: updateSiteRequirements } = useUpdateSiteRequirements()
  const [roofYearEdit, setRoofYearEdit] = useState(
    siteRFPDetails?.roofInstallYear
  )
  const [roofTypeEdit, setRoofTypeEdit] = useState(siteRFPDetails?.roofType)
  const [ownersAssociationEdit, setOwnersAssociationEdit] = useState(
    ownerAssociationBoolToString(siteRFPDetails?.ownersAssociation)
  )
  const [lenderEdit, setLenderEdit] = useState(siteRFPDetails?.lenderName)
  const [lenderApplicableEdit, setLenderApplicableEdit] = useState(
    siteRFPDetails?.lenderApplicable
  )
  const [jvPartnerEdit, setJVPartnerEdit] = useState(
    siteRFPDetails?.jvPartnerName
  )
  const [jvPartnerApplicableEdit, setJVPartnerApplicableEdit] = useState(
    siteRFPDetails?.jvPartnerApplicable
  )
  const [tenantConsentEdit, setTenantConsentEdit] = useState(
    siteRFPDetails?.tenantConsentStatus
  )

  const ownersAssociationDisplay =
    siteRFPDetails?.ownersAssociation === true
      ? 'Yes'
      : siteRFPDetails?.ownersAssociation === false
      ? 'No'
      : null

  const tenantConsentDisplay = siteRFPDetails?.tenantConsentStatus
    ? tenantConsentLabels[siteRFPDetails.tenantConsentStatus]
    : null

  useEffect(() => {
    resetEditValues()
  }, [siteRFPDetails]) //eslint-disable-line react-hooks/exhaustive-deps

  function resetEditValues() {
    if (!siteRFPDetails) {
      return
    }
    setRoofYearEdit(siteRFPDetails.roofInstallYear)
    setRoofTypeEdit(siteRFPDetails.roofType)
    setJVPartnerEdit(siteRFPDetails.jvPartnerName)
    setJVPartnerApplicableEdit(siteRFPDetails.jvPartnerApplicable)
    setLenderEdit(siteRFPDetails.lenderName)
    setLenderApplicableEdit(siteRFPDetails.lenderApplicable)
    setTenantConsentEdit(siteRFPDetails.tenantConsentStatus)
    setOwnersAssociationEdit(
      ownerAssociationBoolToString(siteRFPDetails?.ownersAssociation)
    )
  }

  const isRoofYearEditError =
    roofYearEdit != null &&
    (!Number.isInteger(roofYearEdit) ||
      roofYearEdit < 1800 ||
      roofYearEdit > 2100)
  const isRoofTypeEditError = roofTypeEdit != null && roofTypeEdit.length > 150
  const jvPartnerErrorMsg = getNameErrorMsg(jvPartnerEdit)
  const lenderErrorMsg = getNameErrorMsg(lenderEdit)

  const hasChanges =
    roofYearEdit !== siteRFPDetails?.roofInstallYear ||
    roofTypeEdit !== siteRFPDetails?.roofType ||
    jvPartnerEdit !== siteRFPDetails?.jvPartnerName ||
    jvPartnerApplicableEdit !== siteRFPDetails?.jvPartnerApplicable ||
    lenderEdit !== siteRFPDetails?.lenderName ||
    lenderApplicableEdit !== siteRFPDetails?.lenderApplicable ||
    tenantConsentEdit !== siteRFPDetails?.tenantConsentStatus ||
    ownersAssociationEdit !==
      ownerAssociationBoolToString(siteRFPDetails?.ownersAssociation)

  /**
   * Null values are valid, but strings must be
   * no more than 150 characters long.
   */
  function getNameErrorMsg(value?: string | null): string | null {
    return value != null && value.length > 150
      ? 'Must be <= 150 characters'
      : null
  }

  function ownerAssociationBoolToString(
    ownerAssociation?: boolean | null
  ): OwnersAssociationSelection {
    if (ownerAssociation === true) {
      return 'yes'
    }
    if (ownerAssociation === false) {
      return 'no'
    }
    return 'empty'
  }

  function ownerAssociationStringToBool(
    ownerAssociation: OwnersAssociationSelection
  ): boolean | null {
    if (ownerAssociation === 'yes') {
      return true
    }
    if (ownerAssociation === 'no') {
      return false
    }
    return null
  }
  /**
   * PATCH the new values to the database.
   * The edit button is disabled if `siteRFPDetails` is undefined,
   * so we can assume it is defined when this function is called.
   */
  function handleSave() {
    updateSiteRequirements({
      siteRFPId: siteRFPDetails!.id,
      siteName: siteRFPDetails!.name,
      roofInstallYear: roofYearEdit,
      roofType: roofTypeEdit,
      jvPartnerName: jvPartnerApplicableEdit === false ? null : jvPartnerEdit,
      jvPartnerApplicable: jvPartnerApplicableEdit,
      lenderName: lenderApplicableEdit === false ? null : lenderEdit,
      lenderApplicable: lenderApplicableEdit,
      tenantConsentStatus: tenantConsentEdit,
      ownersAssociation: ownerAssociationStringToBool(ownersAssociationEdit),
      onSuccess: () => setIsEditing(false),
    })
  }

  return (
    <CollapsibleCard
      title="Site Requirements"
      mode={isEditing ? 'edit' : 'view'}
      actions={
        isEditing ? (
          <>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setIsEditing(false)
                resetEditValues()
              }}
              sx={{ padding: '6px 8px', fontSize: '14px' }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              startIcon={<CheckRounded />}
              size="small"
              sx={{
                background: '#8ED6E8',
                color: rucksack.palette.primary.contrastText,
                padding: '6px 16px',
                fontSize: '14px',
              }}
              onClick={() => {
                handleSave()
              }}
              disabled={
                isRoofYearEditError ||
                isRoofTypeEditError ||
                jvPartnerErrorMsg != null ||
                lenderErrorMsg != null ||
                !hasChanges
              }
            >
              Save
            </Button>
          </>
        ) : (
          <IconButton
            aria-label="Edit"
            disabled={!siteRFPDetails}
            onClick={() => setIsEditing(true)}
          >
            <EditRounded />
          </IconButton>
        )
      }
    >
      <CardContentGrid columns={3}>
        {isEditing ? (
          <TextField
            variant="outlined"
            size="medium"
            label="Roof Install Year"
            type="number"
            value={roofYearEdit ?? ''}
            onChange={(event) => {
              const newValue = event.target.value
              setRoofYearEdit(newValue ? parseInt(newValue) : null)
            }}
            error={isRoofYearEditError}
            helperText={
              isRoofYearEditError
                ? 'Must be a whole number >= 1800 and <= 2100'
                : null
            }
          />
        ) : (
          <CardContentField
            label="Roof Install Year"
            value={siteRFPDetails?.roofInstallYear?.toString()}
            isFetching={isFetching}
          />
        )}
        {isEditing ? (
          <TextField
            variant="outlined"
            size="medium"
            label="Roof Type"
            value={roofTypeEdit ?? ''}
            onChange={(input) => {
              const newValue = input.target.value
              setRoofTypeEdit(newValue ? input.target.value : null)
            }}
            error={isRoofTypeEditError}
            helperText={
              isRoofTypeEditError ? 'Must be <= 150 characters' : null
            }
          />
        ) : (
          <CardContentField
            label="Roof Type"
            value={siteRFPDetails?.roofType}
            isFetching={isFetching}
          />
        )}
        {isEditing ? (
          <Select
            id="owner-association-select"
            value={ownersAssociationEdit}
            label="Owners' Association"
            variant="outlined"
            size="medium"
            fullWidth
            autoWidth
            onChange={(event: SelectChangeEvent) =>
              setOwnersAssociationEdit(
                event.target.value as OwnersAssociationSelection
              )
            }
          >
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
            <MenuItem value="empty">Unknown</MenuItem>
          </Select>
        ) : (
          <CardContentField
            label="Owners' Association"
            value={ownersAssociationDisplay}
            isFetching={isFetching}
          />
        )}
        <EditableTextWithNAOption
          label="Lender Name"
          currentValue={lenderEdit}
          currentApplicable={lenderApplicableEdit}
          errorMsg={lenderErrorMsg}
          isEditing={isEditing}
          isFetching={isFetching}
          onChange={({ value, isApplicable }) => {
            setLenderEdit(value)
            setLenderApplicableEdit(isApplicable)
          }}
        />
        <EditableTextWithNAOption
          label="JV Partner Name"
          currentValue={jvPartnerEdit}
          currentApplicable={jvPartnerApplicableEdit}
          errorMsg={jvPartnerErrorMsg}
          isEditing={isEditing}
          isFetching={isFetching}
          onChange={({ value, isApplicable }) => {
            setJVPartnerEdit(value)
            setJVPartnerApplicableEdit(isApplicable)
          }}
        />
        {isEditing ? (
          <Select
            id="tenant-consent-select"
            value={tenantConsentEdit ?? emptyValue}
            label="Tenant Consent Status"
            variant="outlined"
            size="medium"
            fullWidth
            autoWidth
            onChange={(event: SelectChangeEvent) => {
              const newValue = event.target.value
              if (newValue === emptyValue) {
                setTenantConsentEdit(null)
              } else {
                setTenantConsentEdit(newValue as TenantConsentStatusAPIResponse)
              }
            }}
          >
            <MenuItem value={emptyValue}>—</MenuItem>
            {Object.entries(tenantConsentLabels).map(([value, label]) => (
              <MenuItem value={value} key={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <CardContentField
            label="Tenant Consent Status"
            value={tenantConsentDisplay}
            isFetching={isFetching}
          />
        )}
      </CardContentGrid>
    </CollapsibleCard>
  )
}

export interface SiteRequirementsCardProps {
  siteRFPDetails?: SiteRFPDetails | null
  isFetching: boolean
}
